/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import type { CollectionResponse, PageQueryParams } from "@/types/global"
import { HttpRequest } from "@/utils/request"

export interface SetSearchParams extends PageQueryParams {
  search?: string
  type?: string
  visibility?: string
  status?: string
  user_id?: number
  category_id?: number
  parent_id?: number | string | null
  node_type?: string
  has_description?: string
}

export interface SetApiParams {
  page: number
  limit: number
  search?: string
  type?: string
  visibility?: string
  status?: string
  user_id?: number
  category_id?: number
  parent_id?: number | string | null
  node_type?: string
  has_description?: string
}

export interface SetCategoryData {
  id: number
  name: string
  slug: string
  description?: string
}

export interface BreadcrumbNode {
  id: number
  title: string
  node_type: string
  category_id: number
  category?: {
    id: number
    name: string
  }
}

export const setService = {
  list(params: SetApiParams) {
    return HttpRequest.get("v1/sets", {
      searchParams: params as any,
    }).json<CollectionResponse>()
  },
  listCategories() {
    return HttpRequest.get("v1/sets/categories").json<{
      code: number
      message: string
      data: SetCategoryData[]
    }>()
  },
  createCategory(data: { name: string; description?: string }) {
    return HttpRequest.post("v1/sets/categories", {
      json: data,
    }).json<{
      code: number
      message: string
      data: SetCategoryData
    }>()
  },
  create(data: {
    title: string
    full_title?: string | null
    description?: string | null
    tags?: string[] | null
    node_type: "folder" | "set"
    category_id?: number | null
    parent_id?: number | null
    vocabularies?: any[]
    file?: File | null
  }) {
    if (data.file) {
      const formData = new FormData()
      formData.append("title", data.title || "")
      if (data.full_title) {
        formData.append("full_title", data.full_title)
      }
      if (data.description) {
        formData.append("description", data.description)
      }
      if (data.tags !== undefined && data.tags !== null) {
        formData.append("tags", JSON.stringify(data.tags))
      }
      formData.append("node_type", data.node_type)
      if (data.category_id !== undefined && data.category_id !== null) formData.append("category_id", String(data.category_id))
      if (data.parent_id !== undefined && data.parent_id !== null) formData.append("parent_id", String(data.parent_id))
      if (data.vocabularies) formData.append("vocabularies", JSON.stringify(data.vocabularies))
      formData.append("file", data.file)

      return HttpRequest.post("v1/sets", {
        body: formData,
      }).json<{
        code: number
        message: string
        data: any
      }>()
    }

    return HttpRequest.post("v1/sets", {
      json: data,
    }).json<{
      code: number
      message: string
      data: any
    }>()
  },
  get(id: number) {
    return HttpRequest.get(`v1/sets/${id}`).json<{
      code: number
      message: string
      data: any
    }>()
  },
  update(id: number, data: { title?: string; full_title?: string | null; slug?: string | null; tags?: string[] | null; description?: string | null; image?: string | null; file?: File | null }) {
    if (data.file) {
      const formData = new FormData()
      if (data.title !== undefined) formData.append("title", data.title || "")
      if (data.full_title !== undefined && data.full_title !== null) {
        formData.append("full_title", data.full_title)
      } else if (data.full_title === null) {
        formData.append("full_title", "")
      }
      if (data.slug !== undefined && data.slug !== null) {
        formData.append("slug", data.slug)
      } else if (data.slug === null) {
        formData.append("slug", "")
      }
      if (data.tags !== undefined && data.tags !== null) {
        formData.append("tags", JSON.stringify(data.tags))
      }
      if (data.description !== undefined && data.description !== null) {
        formData.append("description", data.description)
      } else if (data.description === null) {
        formData.append("description", "")
      }
      if (data.image === null) {
        formData.append("image", "")
      }
      formData.append("file", data.file)

      return HttpRequest.put(`v1/sets/${id}`, {
        body: formData,
      }).json<{
        code: number
        message: string
        data: any
      }>()
    }

    return HttpRequest.put(`v1/sets/${id}`, {
      json: data,
    }).json<{
      code: number
      message: string
      data: any
    }>()
  },
  delete(id: number) {
    return HttpRequest.delete(`v1/sets/${id}`).json<{
      code: number
      message: string
    }>()
  },
  validateVocabularies(data: { vocabularies: { text: string; part_of_speech: string }[] }) {
    return HttpRequest.post("v1/sets/validate-vocabularies", {
      json: data,
      timeout: false,
    }).json<{
      code: number
      message: string
      data: any[]
    }>()
  },
  moveSets(data: { ids: number[]; target_parent_id: number | null }) {
    return HttpRequest.put("v1/sets/move", {
      json: data,
    }).json<{
      code: number
      message: string
    }>()
  },
  reorder(items: { id: number; order_index: number }[]) {
    return HttpRequest.put("v1/sets/reorder", {
      json: { items },
    }).json<{
      code: number
      message: string
    }>()
  },
  getBreadcrumbs(id: number | string) {
    return HttpRequest.get(`v1/sets/${id}/breadcrumbs`).json<{
      code: number
      message: string
      data: BreadcrumbNode[]
    }>()
  },
}
